import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import '../CSS/Footer.css';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

const BRANCHES = {
  all:     { lat: 19.245086, lng: 73.169978 },
  century: { lat: 19.245086, lng: 73.169978 },
  khemani: { lat: 19.232974664054428, lng: 73.16700843133822 },
};

const Footer = () => {
  const [branch, setBranch] = useState('all');
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef([]);

  useEffect(() => {
    if (!mapObj.current) {
      mapObj.current = L.map(mapRef.current).setView([BRANCHES.all.lat, BRANCHES.all.lng], 14);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19 }).addTo(mapObj.current);
    }
  }, []);

  useEffect(() => {
    markers.current.forEach(m => m.remove());
    markers.current = [];
    const show = branch === 'all' ? ['century', 'khemani'] : [branch];
    show.forEach(b => {
      const m = L.marker([BRANCHES[b].lat, BRANCHES[b].lng]).addTo(mapObj.current);
      markers.current.push(m);
    });
    if (branch !== 'all') {
      mapObj.current.setView([BRANCHES[branch].lat, BRANCHES[branch].lng], 16);
    } else {
      mapObj.current.setView([BRANCHES.all.lat, BRANCHES.all.lng], 14);
    }
  }, [branch]);

  const gmapsUrl = branch !== 'all'
    ? `https://www.google.com/maps/dir/?api=1&destination=${BRANCHES[branch].lat},${BRANCHES[branch].lng}`
    : '#';

  return (
    <footer className="ngt-footer" itemScope itemType="https://schema.org/EducationalOrganization">
      <meta itemProp="name" content="Nalanda Group Tuitions" />

      <div className="ngt-footer-top">
        <div className="container">
          <div className="row g-5">
            {/* About Column */}
            <div className="col-lg-3 col-md-6">
              <h5>About NGT</h5>
              <p itemProp="description">
                Nalanda Group Tuitions has provided quality education for over 21 years, helping
                students excel in SSC, CBSE, JEE, NEET, MH-CET and Commerce streams in Ulhasnagar.
              </p>
              <div className="ngt-social" aria-label="Social Media Links">
                <a href="https://www.facebook.com/profile.php?id=100090621790156" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href="https://www.instagram.com/nalandagrouptuitions/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="https://wa.me/+919272136659" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                  <i className="fab fa-whatsapp"></i>
                </a>
                <a href="tel:+919272136659" aria-label="Call Us">
                  <i className="fas fa-phone"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2 col-md-6">
              <h5>Quick Links</h5>
              <ul className="ngt-footer-links">
                <li><Link to="/">Home</Link></li>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/ssc-coaching-ulhasnagar-kalyan">SSC Courses</Link></li>
                <li><Link to="/cbse-coaching-ulhasnagar-kalyan">CBSE Courses</Link></li>
                <li><Link to="/science-coaching-ulhasnagar-kalyan">Science / JEE / NEET</Link></li>
                <li><Link to="/commerce-classes-ulhasnagar-kalyan">Commerce</Link></li>
                <li><Link to="/exam">Exams</Link></li>
                <li><Link to="/faculty">Faculty</Link></li>
                <li><Link to="/gallery">Gallery</Link></li>
                <li><Link to="/testimony">Testimonials</Link></li>
                <li><Link to="/enquiry">Enquiry</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div className="col-lg-3 col-md-6">
              <h5>Contact Us</h5>
              <div className="ngt-footer-contact">
                <p>
                  <i className="fas fa-map-marker-alt"></i>
                  <span itemProp="address">Deshmukh Plaza, Murbad Road, Opp Century School, Ulhasnagar</span>
                </p>
                <p>
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Near Jhulelal School, Khemani, Ulhasnagar</span>
                </p>
                <p>
                  <i className="fas fa-phone"></i>
                  <a href="tel:+919272136659" style={{ color: 'rgba(255,255,255,0.75)' }} itemProp="telephone">+91 9272136659</a>
                </p>
                <p>
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:ngt.mailer@gmail.com" style={{ color: 'rgba(255,255,255,0.75)' }} itemProp="email">ngt.mailer@gmail.com</a>
                </p>
                <p>
                  <i className="fas fa-clock"></i>
                  <span>Mon – Sat: 7:00 AM – 8:00 PM</span>
                </p>
              </div>
            </div>

            {/* Branch Map */}
            <div className="col-lg-4 col-md-6">
              <h5>Find Our Branches</h5>
              <select
                className="ngt-branch-select"
                value={branch}
                onChange={e => setBranch(e.target.value)}
                aria-label="Select branch"
              >
                <option value="all">All Branches</option>
                <option value="century">Century Branch (Murbad Road)</option>
                <option value="khemani">Khemani Branch</option>
              </select>
              {branch !== 'all' && (
                <a
                  href={gmapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ngt-gold d-inline-block mb-2"
                  style={{ fontSize: '0.85rem', padding: '7px 16px' }}
                >
                  <i className="fas fa-directions me-1"></i> Get Directions
                </a>
              )}
              <div className="ngt-footer-map">
                <div ref={mapRef} style={{ width: '100%', height: '160px' }} aria-label="Map showing NGT branch locations"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="ngt-footer-bottom">
        <div className="container d-flex flex-column flex-md-row justify-content-between gap-1">
          <span>&copy; 2026 Nalanda Group Tuitions. All Rights Reserved.</span>
          <span>
            <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.5)' }}>Privacy Policy</Link>
            <span className="mx-2">·</span>
            <Link to="/sitemap.xml" style={{ color: 'rgba(255,255,255,0.5)' }}>Sitemap</Link>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
